import { isoDateToPaidAt } from '../components/expensePaymentFlow';
import { createExpense, settleExpense, voidExpense } from '../infra/expensesRepo'
import { todayIsoDate } from '../infra/expenseListSql'
import { saveExpenseAttachmentLocal } from '../infra/expenseAttachmentsRepo';
import type { ExpenseRegisterSubmitInput } from './expenseRegisterSubmitTypes';

export type { ExpenseRegisterSubmitInput } from './expenseRegisterSubmitTypes';
export {
  validateExpenseRegisterBusinessRules,
  shouldConfirmCashDrawerOverExpected,
  expenseRegisterSuccessCopy,
} from './expenseRegisterSubmitTypes';

export type ExpenseRegisterSubmitContext = {
  organizationId: string;
  branchId: string;
  activeCashSessionId: string | null;
  createdBy: string | null;
};

export type ExpenseRegisterResult = {
  expenseId: string;
  attachmentFailed?: boolean;
};

export async function runWithCompensateOnFailure<T>(
  action: () => Promise<T>,
  compensate: () => Promise<void>
): Promise<T> {
  try {
    return await action();
  } catch (error) {
    try {
      await compensate();
    } catch (compensateError) {
      console.warn('[expenses] compensate after failure', compensateError);
    }
    throw error;
  }
}

export async function executeExpenseRegister(
  input: ExpenseRegisterSubmitInput,
  ctx: ExpenseRegisterSubmitContext,
  amount: number
): Promise<ExpenseRegisterResult> {
  const expenseDate = input.expenseDate.trim() || todayIsoDate();

  const expenseId = await createExpense({
    organizationId: ctx.organizationId,
    branchId: ctx.branchId,
    categorySlug: input.categorySlug,
    amount,
    expenseDate,
    dueDate: input.dueDate ?? null,
    description: input.description.trim() || null,
    receiptRef: input.receiptRef.trim() || null,
    supplierId: input.supplierId ?? null,
    createdBy: ctx.createdBy,
    fiscal: input.fiscal ?? null,
  });

  if (input.paymentIntent === 'pay_now') {
    await runWithCompensateOnFailure(
      () =>
        settleExpense({
          expenseId,
          branchId: ctx.branchId,
          method: input.settleMethod,
          amount,
          categorySlug: input.categorySlug,
          cashSessionId: input.settleMethod === 'cash_drawer' ? ctx.activeCashSessionId : null,
          note: input.settleNote.trim() || null,
          paidAt:
            input.settleMethod === 'cash_drawer'
              ? undefined
              : isoDateToPaidAt(input.paidDate || todayIsoDate()),
          createdBy: ctx.createdBy,
        }),
      () =>
        voidExpense({
          expenseId,
          branchId: ctx.branchId,
          voidedBy: ctx.createdBy ?? undefined,
          voidReason: 'No se pudo registrar el pago.',
        })
    );
  }

  if (input.attachment) {
    try {
      await saveExpenseAttachmentLocal({
        attachmentId: crypto.randomUUID(),
        expenseId,
        branchId: ctx.branchId,
        orgId: ctx.organizationId,
        fileName: input.attachment.fileName,
        mimeType: input.attachment.mimeType,
        fileSize: input.attachment.fileSize,
        fileBase64: input.attachment.base64,
        createdBy: ctx.createdBy,
      });
    } catch (attachError) {
      console.warn('[executeExpenseRegister] attachment failed', attachError);
      return { expenseId, attachmentFailed: true };
    }
  }

  return { expenseId };
}
